import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useData } from '../../contexts/DataContext';
import { ArticleAutocomplete } from '../../components/ui/article-autocomplete';
import { 
  MapPin, Package, Layers, Grid3X3, QrCode, 
  Smartphone, Camera, X, Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Html5Qrcode } from 'html5-qrcode';
import { useQrScanner } from '../../hooks/useQrScanner';
import { StockLotsDialog } from '../../components/StockLotsDialog';
import { BinArticleSelector, parseBinQrCode } from '../../components/ui/bin-article-selector';

const StockLocatorPage = () => {
  const { articles, fetchArticles } = useData();
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLotsDialog, setShowLotsDialog] = useState(false);
  const [binScanOpen, setBinScanOpen] = useState(false);
  const [binScanCode, setBinScanCode] = useState('');
  const [binScanType, setBinScanType] = useState('bin');
  const scannerRef = useRef(null);
  const { start: startQr, stop: stopQr } = useQrScanner();

  useEffect(() => {
    fetchArticles();
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
  }, [fetchArticles]);

  const handleArticleSelect = async (article) => {
    if (!article) {
      setSelectedArticle(null);
      setResult(null);
      return;
    }

    setSelectedArticle(article);
    
    // Auto-search on selection
    try {
      setLoading(true);
      const response = await api.get(`/stock/locate?article_id=${article.id}`);
      setResult(response.data);
      
      if (response.data.locations_count === 0) {
        toast.warning('Articol fara stoc disponibil');
      } else {
        toast.success(`Gasit in ${response.data.locations_count} locatii`);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Eroare la cautare');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const startQrScanner = async () => {
    setShowScanner(true);
    setTimeout(async () => {
      try {
        await startQr('qr-reader', async (decodedText) => {
          // Check if BIN QR
          const binResult = parseBinQrCode(decodedText);
          if (binResult.isBin) {
            await stopQrScanner();
            setBinScanCode(binResult.value);
            setBinScanType(binResult.type);
            setBinScanOpen(true);
            return;
          }
          // Parse ART: prefix
          let searchCode = decodedText;
          const parts = decodedText.split(':');
          if (parts.length >= 2 && parts[0] === 'ART') searchCode = parts.slice(1).join(':');
          const article = articles.find(a =>
            a.code === searchCode ||
            searchCode.includes(a.code) ||
            a.name.toLowerCase().includes(searchCode.toLowerCase())
          );
          if (article) {
            await stopQrScanner();
            toast.success('Articol gasit!');
            handleArticleSelect(article);
          } else {
            toast.warning(`Cod scanat: ${decodedText}. Cauta manual.`);
          }
        }, { fps: 10, qrbox: { width: 250, height: 250 } });
      } catch (error) {
        console.error('Scanner error:', error);
        toast.error('Eroare camera. Verifica permisiunile.');
        setShowScanner(false);
      }
    }, 100);
  };

  const handleBinArticleSelect = (art) => {
    setBinScanOpen(false);
    const article = articles.find(a => a.id === art.article_id);
    if (article) {
      handleArticleSelect(article);
    } else {
      handleArticleSelect({ id: art.article_id, name: art.article_name, code: art.article_code, unit: art.unit });
    }
  };

  const stopQrScanner = async () => {
    await stopQr();
    setShowScanner(false);
  };

  return (
    <div className="pb-24 lg:pb-6">
      <BinArticleSelector open={binScanOpen} binCode={binScanCode} scanType={binScanType} onSelect={handleBinArticleSelect} onClose={() => setBinScanOpen(false)} />
      {/* Header - Mobile Optimized */}
      <div className="sticky top-0 bg-slate-50 z-10 pb-4 pt-3 border-b">
        <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 flex items-center gap-2">
          <Package className="w-8 h-8 text-blue-600" />
          Unde este?
        </h1>
        <p className="text-base lg:text-lg text-slate-600 mt-2">
          Cauta articole instant
        </p>
      </div>

      <div className="p-4 space-y-4">
        {/* Autocomplete Search - REAL */}
        <Card className="shadow-xl border-2 border-blue-200">
          <CardContent className="p-5">
            <ArticleAutocomplete
              articles={articles}
              onSelect={handleArticleSelect}
              placeholder={isMobile ? "Tasteza articol..." : "Cauta articol (ex: surub, piulita...)"}
            />
            
            <Button
              onClick={startQrScanner}
              variant="outline"
              className="w-full h-14 text-lg font-semibold mt-3"
              size="lg"
            >
              <Camera className="w-6 h-6 mr-2" />
              SAU Scan QR/Barcode
            </Button>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card>
            <CardContent className="p-8 text-center">
              <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-3 animate-spin" />
              <p className="text-lg font-semibold">Cautare in curs...</p>
            </CardContent>
          </Card>
        )}

        {/* Results Section - Mobile Optimized */}
        {result && !loading && (
          <div className="space-y-4">
            {/* Article Info Card */}
            <Card className="border-4 border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 shadow-2xl">
              <CardContent className="p-6">
                <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-2">{result.article.name}</h2>
                <p className="text-lg text-slate-700 font-mono font-semibold mb-4">{result.article.code}</p>
                
                <div className="bg-white rounded-xl p-5 shadow-lg text-center">
                  <p className="text-base text-slate-600 mb-1">Stoc Total Disponibil</p>
                  <p className="text-6xl font-black text-green-600 mb-1">{result.total_quantity}</p>
                  <p className="text-2xl font-semibold text-slate-700">{result.article.unit}</p>
                </div>
                <Button 
                  onClick={() => setShowLotsDialog(true)} 
                  variant="outline" 
                  className="w-full mt-3 h-12 text-base font-semibold border-2"
                  data-testid="view-lots-btn"
                >
                  <Layers className="w-5 h-5 mr-2" /> Vezi Loturi per Furnizor
                </Button>
              </CardContent>
            </Card>

            {/* Preferred BIN - Always show first if exists */}
            {result.article.default_bin_location && (
              <Card className="border-4 border-blue-400 bg-gradient-to-br from-blue-50 to-white shadow-xl mb-4">
                <CardContent className="p-5">
                  <Badge className="bg-blue-600 mb-3">🎯 LOCATIE PREFERATA</Badge>
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">Acesta este BIN-ul preferat pentru stocare:</p>
                    <p className="text-2xl font-bold text-blue-900">{result.article.default_bin_location}</p>
                    <p className="text-xs text-slate-500">Cod: {result.article.default_bin_code}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Locations */}
            {result.locations.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Package className="w-20 h-20 text-orange-500 mx-auto mb-4" />
                  <p className="text-xl font-bold text-slate-700">Stoc epuizat</p>
                  <p className="text-base text-slate-500 mt-2">Nu exista stoc in nicio locatie</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-slate-900 px-1">
                  📍 {result.locations_count} {result.locations_count === 1 ? 'Locatie' : 'Locatii'}
                </h3>
                
                {result.locations.map((location, index) => (
                  <Card key={index} className="shadow-2xl border-4 border-slate-200 hover:border-blue-400 transition-all">
                    <CardContent className="p-6">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg">
                            <MapPin className="w-8 h-8 text-white" />
                          </div>
                          <div>
                            <p className="text-2xl font-black text-slate-900">#{index + 1}</p>
                            <p className="text-sm text-slate-600">{location.plant_name}</p>
                          </div>
                        </div>
                        <Badge className="bg-green-600 text-white text-xl px-4 py-2 shadow-lg">
                          {location.quantity} {location.unit}
                        </Badge>
                      </div>

                      {/* BIN Path - HUGE for easy reading */}
                      <div className="space-y-3 mb-5">
                        <div className="p-5 bg-gradient-to-br from-blue-100 via-blue-50 to-white rounded-xl border-4 border-blue-300 shadow-lg">
                          <p className="text-sm font-semibold text-blue-700 mb-1">CABINET</p>
                          <p className="text-4xl font-black text-blue-900">{location.cabinet}</p>
                        </div>
                        
                        <div className="p-5 bg-gradient-to-br from-purple-100 via-purple-50 to-white rounded-xl border-4 border-purple-300 shadow-lg">
                          <p className="text-sm font-semibold text-purple-700 mb-1">RAFT</p>
                          <p className="text-4xl font-black text-purple-900">{location.shelf}</p>
                        </div>
                        
                        <div className="p-5 bg-gradient-to-br from-green-100 via-green-50 to-white rounded-xl border-4 border-green-400 shadow-2xl animate-pulse">
                          <p className="text-base font-bold text-green-700 mb-1">🎯 POZITIE FINALA</p>
                          <p className="text-5xl font-black text-green-900">{location.position}</p>
                        </div>
                      </div>

                      {/* BIN Code */}
                      <div className="bg-slate-900 p-4 rounded-xl mb-4 shadow-lg">
                        <p className="text-xs text-slate-400 mb-1">Cod BIN:</p>
                        <p className="font-mono font-bold text-xl text-white">{location.bin_code}</p>
                      </div>

                      {/* Copy Button - HUGE */}
                      <Button
                        onClick={() => {
                          const locationText = `${location.cabinet} → ${location.shelf} → ${location.position}`;
                          navigator.clipboard.writeText(locationText);
                          toast.success('Locatie copiata!', {
                            description: locationText,
                            duration: 3000
                          });
                        }}
                        className="w-full h-16 text-xl font-bold shadow-xl"
                      >
                        <QrCode className="w-6 h-6 mr-3" />
                        Copiaza Locatie
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!result && !loading && (
          <Card className="border-dashed border-4 border-slate-300 shadow-lg">
            <CardContent className="p-10 text-center">
              <Package className="w-24 h-24 text-slate-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-slate-700 mb-2">
                Incepe sa tastezi
              </h3>
              <p className="text-lg text-slate-500">
                Introdu numele articolului si alege din lista
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* QR Scanner Dialog - Fixed */}
      <Dialog open={showScanner} onOpenChange={(open) => {
        if (!open) stopQrScanner();
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between text-xl">
              <span className="flex items-center gap-2">
                <Camera className="w-6 h-6 text-blue-600" />
                Scaneaza Cod
              </span>
              <Button variant="ghost" size="sm" onClick={stopQrScanner}>
                <X className="w-5 h-5" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Scanner Container with Guide */}
            <div className="relative bg-black rounded-xl overflow-hidden" style={{ minHeight: '400px' }}>
              <div id="qr-reader" className="w-full" />
              
              {/* Visual Guide Frame */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative">
                  {/* Animated corners */}
                  <div className="w-72 h-72 relative">
                    {/* Top-left corner */}
                    <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-green-400 animate-pulse" />
                    {/* Top-right corner */}
                    <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-green-400 animate-pulse" />
                    {/* Bottom-left corner */}
                    <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-green-400 animate-pulse" />
                    {/* Bottom-right corner */}
                    <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-green-400 animate-pulse" />
                    {/* Center text */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-black/50 px-4 py-2 rounded-lg">
                        <p className="text-white font-bold text-sm">Pozitioneaza codul aici</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border-2 border-green-300">
              <p className="text-base font-bold text-green-900 text-center">
                Tine telefonul stabil
              </p>
              <p className="text-sm text-green-700 text-center mt-1">
                Scanner-ul va detecta automat codul QR sau barcode
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Lots Dialog */}
      {selectedArticle && (
        <StockLotsDialog
          open={showLotsDialog}
          onOpenChange={setShowLotsDialog}
          articleId={selectedArticle.id}
          articleName={selectedArticle.name}
          onConsumed={() => { if (selectedArticle) handleArticleSelect(selectedArticle); }}
        />
      )}
    </div>
  );
};

export default StockLocatorPage;
